/**
 * OfflinePresets — convenience factories that build SDK Layers/Sources
 * pointed at a registered MIAirgapConfig bundle. They keep the consumer
 * away from MapLibre/Cesium engine types entirely; the returned objects
 * are the same translator-pattern primitives used everywhere else in the
 * SDK (GeoJsonSource, VectorLayer, Tileset3DLayer).
 */
import { MIAirgapConfig } from './MIAirgapConfig';
import { GeoJsonSource } from '../sources/Source';
import { VectorLayer } from '../layers/Layer';
import { Tileset3DLayer } from '../layers/Layer3D';
import { Style, Fill, Stroke, CircleStyle } from '../style/Style';

// ─── Minimal typed subset of the MapLibre style spec we consume ───────
// (We only support the layer/source kinds present in airgap bundles.)

type RGBAColor = string;
type LngLatTuple = [number, number];

interface StyleGeoJsonSource {
  type: 'geojson';
  /** URL string OR inline FeatureCollection. */
  data: string | GeoJSON.FeatureCollection;
}

interface BackgroundLayer {
  id: string;
  type: 'background';
  paint?: { 'background-color'?: RGBAColor };
}
interface FillLayer {
  id: string;
  type: 'fill';
  source: string;
  filter?: StyleFilter;
  paint?: { 'fill-color'?: RGBAColor; 'fill-opacity'?: number };
}
interface LineLayer {
  id: string;
  type: 'line';
  source: string;
  filter?: StyleFilter;
  paint?: { 'line-color'?: RGBAColor; 'line-width'?: number; 'line-opacity'?: number; 'line-dasharray'?: number[] };
}
interface CircleLayer {
  id: string;
  type: 'circle';
  source: string;
  filter?: StyleFilter;
  paint?: {
    'circle-radius'?: number;
    'circle-color'?: RGBAColor;
    'circle-stroke-color'?: RGBAColor;
    'circle-stroke-width'?: number;
  };
}
type StyleLayer = BackgroundLayer | FillLayer | LineLayer | CircleLayer;
type StyleFilter =
  | ['==', ['get', string] | string, string | number | boolean]
  | unknown[]; // any other filter passes through unfiltered

interface MapLibreStyleSubset {
  version?: number;
  name?: string;
  sources?: Record<string, StyleGeoJsonSource | { type: string; [k: string]: unknown }>;
  layers?: StyleLayer[];
}

function getCfg(): MIAirgapConfig {
  const cfg = MIAirgapConfig.get();
  if (!cfg) throw new Error('OfflinePresets: MIAirgapConfig has not been configured. Call MIAirgapConfig.configure({ ... }) and registerBundle()/loadManifest() first.');
  return cfg;
}

export interface OfflineBasemapOptions {
  /** Bundle name registered in MIAirgapConfig (default: first registered bundle). */
  bundle?: string;
  /** Style file inside the bundle's basemap directory (default: 'style.json'). */
  styleFile?: string;
  /** Optional id prefix (default: `airgap-basemap-<bundle>`). */
  id?: string;
}

/**
 * OfflineBasemap — returns an array of MIGIS Layers that visually reproduce
 * the bundle's MapLibre style (background + GeoJSON fill/line/circle layers).
 * Consumers add the layers via `MIMap.addLayer(...)` like any other layer.
 */
export async function OfflineBasemap(opts: OfflineBasemapOptions = {}): Promise<VectorLayer[]> {
  const cfg = getCfg();
  const bundleName = opts.bundle ?? cfg.listBundles()[0]?.name;
  if (!bundleName) throw new Error('OfflineBasemap: no bundles registered.');
  const styleUrl = cfg.bundlePath(bundleName, 'basemap', opts.styleFile ?? 'style.json');
  const idPrefix = opts.id ?? `airgap-basemap-${bundleName}`;

  const res = await fetch(styleUrl);
  if (!res.ok) throw new Error(`OfflineBasemap: cannot load ${styleUrl}: ${res.status}`);
  const style = (await res.json()) as MapLibreStyleSubset;

  // Pre-fetch all geojson sources so they become inline data the SDK GeoJsonSource can hold.
  const sourceData: Record<string, GeoJSON.FeatureCollection> = {};
  for (const [srcId, src] of Object.entries(style.sources ?? {})) {
    if (src.type !== 'geojson') continue;
    const gjs = src as StyleGeoJsonSource;
    if (typeof gjs.data === 'string') {
      const r = await fetch(gjs.data);
      if (!r.ok) throw new Error(`OfflineBasemap: cannot load source '${srcId}' from ${gjs.data}`);
      sourceData[srcId] = (await r.json()) as GeoJSON.FeatureCollection;
    } else {
      sourceData[srcId] = gjs.data;
    }
  }

  const layers: VectorLayer[] = [];
  for (const layer of style.layers ?? []) {
    if (layer.type === 'background') {
      const worldFc: GeoJSON.FeatureCollection = {
        type: 'FeatureCollection',
        features: [{
          type: 'Feature', properties: {},
          geometry: { type: 'Polygon', coordinates: [[[-180,-85],[180,-85],[180,85],[-180,85],[-180,-85]] as LngLatTuple[]] },
        }],
      };
      layers.push(new VectorLayer({
        id: `${idPrefix}-${layer.id}`,
        source: new GeoJsonSource({ data: worldFc }),
        style: new Style({ fill: new Fill({ color: layer.paint?.['background-color'] ?? '#000' }) }),
      }));
      continue;
    }
    const data = sourceData[layer.source];
    if (!data) continue;
    const filtered = applyEqualityFilter(data, layer.filter);
    const styleObj = mapPaintToStyle(layer);
    const opacity = layer.type === 'fill'
      ? layer.paint?.['fill-opacity'] ?? 1
      : layer.type === 'line'
        ? layer.paint?.['line-opacity'] ?? 1
        : 1;
    layers.push(new VectorLayer({
      id: `${idPrefix}-${layer.id}`,
      source: new GeoJsonSource({ data: filtered }),
      style: styleObj,
      opacity,
    }));
  }
  return layers;
}

function applyEqualityFilter(fc: GeoJSON.FeatureCollection, filter?: StyleFilter): GeoJSON.FeatureCollection {
  if (!filter || !Array.isArray(filter) || filter[0] !== '==') return fc;
  const expr = filter[1];
  const value = filter[2];
  let key: string | null = null;
  if (Array.isArray(expr) && expr[0] === 'get' && typeof expr[1] === 'string') key = expr[1];
  else if (typeof expr === 'string') key = expr;
  if (!key) return fc;
  return {
    ...fc,
    features: fc.features.filter((f) => f.properties && (f.properties as Record<string, unknown>)[key!] === value),
  };
}

function mapPaintToStyle(layer: FillLayer | LineLayer | CircleLayer): Style {
  if (layer.type === 'fill') {
    return new Style({ fill: new Fill({ color: layer.paint?.['fill-color'] ?? '#888' }) });
  }
  if (layer.type === 'line') {
    return new Style({
      stroke: new Stroke({
        color: layer.paint?.['line-color'] ?? '#444',
        width: layer.paint?.['line-width'] ?? 1,
        ...(layer.paint?.['line-dasharray'] ? { lineDash: layer.paint['line-dasharray'] } : {}),
      }),
    });
  }
  return new Style({
    image: new CircleStyle({
      radius: layer.paint?.['circle-radius'] ?? 4,
      fill: new Fill({ color: layer.paint?.['circle-color'] ?? '#3b82f6' }),
      stroke: new Stroke({
        color: layer.paint?.['circle-stroke-color'] ?? '#fff',
        width: layer.paint?.['circle-stroke-width'] ?? 1,
      }),
    }),
  });
}

export interface OfflineTerrainOptions {
  bundle?: string;
  /** File inside dted/ (default: 'elevation.json'). */
  file?: string;
}

/** Shape of the elevation grid JSON shipped in airgap bundles. */
export interface OfflineElevationGrid {
  cols: number; rows: number;
  minLon: number; minLat: number;
  cellLon: number; cellLat: number;
  units: 'm' | 'ft';
  data: number[];
  /** Sample the grid at (lon, lat). Returns null if outside bounds. */
  sample: (lon: number, lat: number) => number | null;
  /** URL of the underlying file (useful for engine-level providers). */
  url: string;
}

interface RawElevationGrid {
  cols: number; rows: number;
  minLon: number; minLat: number;
  cellLon: number; cellLat: number;
  units: 'm' | 'ft';
  data: number[];
}

/**
 * OfflineTerrain — returns the bundled elevation grid plus a sampler.
 */
export async function OfflineTerrain(opts: OfflineTerrainOptions = {}): Promise<OfflineElevationGrid> {
  const cfg = getCfg();
  const bundleName = opts.bundle ?? cfg.listBundles().find((b) => b.contains.includes('dted'))?.name;
  if (!bundleName) throw new Error('OfflineTerrain: no bundle with dted contents.');
  const url = cfg.bundlePath(bundleName, 'dted', opts.file ?? 'elevation.json');
  const res = await fetch(url);
  if (!res.ok) throw new Error(`OfflineTerrain: cannot load ${url}: ${res.status}`);
  const g = (await res.json()) as RawElevationGrid;
  const sample = (lon: number, lat: number): number | null => {
    const c = Math.floor((lon - g.minLon) / g.cellLon);
    const r = Math.floor((lat - g.minLat) / g.cellLat);
    if (c < 0 || c >= g.cols || r < 0 || r >= g.rows) return null;
    return g.data[r * g.cols + c];
  };
  return { ...g, sample, url };
}

export interface OfflineTilesetOptions {
  bundle?: string;
  file?: string;
  id?: string;
}

/**
 * OfflineTileset — returns a Tileset3DLayer pointing at the bundle's
 * `3dtiles/tileset.json`. Drop into `MISceneView.addLayer(...)`.
 */
export function OfflineTileset(opts: OfflineTilesetOptions = {}): Tileset3DLayer {
  const cfg = getCfg();
  const bundleName = opts.bundle ?? cfg.listBundles().find((b) => b.contains.includes('3dtiles'))?.name;
  if (!bundleName) throw new Error('OfflineTileset: no bundle with 3dtiles contents.');
  const url = cfg.bundlePath(bundleName, '3dtiles', opts.file ?? 'tileset.json');
  return new Tileset3DLayer({ id: opts.id ?? `airgap-tileset-${bundleName}`, url });
}

/**
 * Convenience: load the demo manifest into a fresh MIAirgapConfig
 * (with strict mode opt-in via `extra`) and return it ready for the presets.
 */
export async function configureFromManifest(
  manifestUrl = '/api/v2/airgap/manifest',
  extra?: Parameters<typeof MIAirgapConfig.configure>[0],
): Promise<MIAirgapConfig> {
  const cfg = MIAirgapConfig.configure({ enabled: true, ...(extra ?? {}) });
  await cfg.loadManifest(manifestUrl);
  return cfg;
}
